import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { InventoryKeypad, parseEurToCents, centsToEur } from '../../components/inventory/InventoryKeypad';
import { Boxes, Recycle, Package, Plus, Settings2, X, FileText, CheckCircle2, Clock, Pencil, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}` } });
export const MANUAL_UI_VERSION = 'manual-ui-2026-10-30-gating';

export default function ManualInventoryList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [invType, setInvType] = useState('pfand');
  const [inventories, setInventories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showTpl, setShowTpl] = useState(false);
  const [showSL, setShowSL] = useState(false);
  const [locked, setLocked] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/manual-inventory`, authHeader());
      setInventories(res.data || []);
    } catch (e) {
      if (e?.response?.status === 403) setLocked(true);
      else toast.error('Inventuren konnten nicht geladen werden');
    }
    finally { setLoading(false); }
  };
  const loadLocations = async () => {
    try {
      const res = await axios.get(`${API}/api/user/locations`, authHeader());
      const locs = res.data || [];
      setLocations(locs);
      if (locs.length === 1) setLocationId(locs[0].id);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); loadLocations(); }, []);

  const start = async () => {
    if (locations.length > 1 && !locationId) { toast.error('Bitte einen Standort wählen'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/manual-inventory`,
        { inv_type: invType, location_id: locationId || null, inventory_date: date }, authHeader());
      navigate(`/inventur/manuell/${res.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Start fehlgeschlagen'); }
    finally { setCreating(false); }
  };

  const typeLabel = (t) => t === 'pfand' ? 'Pfand' : 'Produkte / Verbrauchsmaterial';

  if (locked) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="inventory-locked-notice">
          <div className="h-14 w-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-5">
            <Lock className="h-7 w-7 text-amber-600 dark:text-amber-300" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Inventur ist ab Business verfügbar</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Das Inventurmodul (Presse- und manuelle Inventur mit Pfand, Produkten, Vorlagen und PDF/CSV-Export)
            ist Teil der Pakete <strong>Business</strong> und <strong>Professional AI</strong>.
            Ihr aktuelles Paket enthält diese Funktion nicht.
          </p>
          <Button data-testid="inventory-upgrade-btn" onClick={() => navigate('/admin/subscription')}>
            Paket ansehen
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-5 space-y-5" data-testid="manual-inventory-list">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
              <Boxes className="h-6 w-6 text-teal-600 dark:text-teal-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Manuelle Inventur</h1>
              <p className="text-sm text-muted-foreground">Pfand und Produkte/Verbrauchsmaterial zählen und auswerten</p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex gap-2 flex-wrap">
              <Button data-testid="manage-templates-btn" variant="outline" size="sm" className="gap-1.5" onClick={() => setShowTpl(true)}>
                <Settings2 className="h-4 w-4" /> Vorlagen
              </Button>
              <Button data-testid="manual-sl-perm-btn" variant="outline" size="sm" className="gap-1.5" onClick={() => setShowSL(true)}>
                <Settings2 className="h-4 w-4" /> Wiederöffnen-Rechte
              </Button>
            </div>
          )}
        </div>

        {/* Start-Flow */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Typ wählen</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button data-testid="type-pfand" onClick={() => setInvType('pfand')}
                className={`flex items-center gap-2 p-3 rounded-xl border text-left ${invType === 'pfand' ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/20' : 'border-border'}`}>
                <Recycle className="h-5 w-5 text-teal-600" /> <span className="font-medium text-foreground">Pfand</span>
              </button>
              <button data-testid="type-product" onClick={() => setInvType('product')}
                className={`flex items-center gap-2 p-3 rounded-xl border text-left ${invType === 'product' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/20' : 'border-border'}`}>
                <Package className="h-5 w-5 text-indigo-600" /> <span className="font-medium text-foreground">Produkte / Verbrauch</span>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Standort</label>
              {locations.length <= 1 ? (
                <div data-testid="single-location" className="mt-1 px-3 py-2 rounded-lg border border-border bg-muted/40 text-sm text-foreground">
                  {locations[0]?.name || 'Kein Standort verfügbar'}
                </div>
              ) : (
                <select data-testid="location-select" value={locationId} onChange={e => setLocationId(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground">
                  <option value="">– bitte wählen –</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Inventurdatum</label>
              <input data-testid="date-input" type="date" value={date} onChange={e => setDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground" />
            </div>
          </div>
          <Button data-testid="start-btn" className="w-full gap-1.5" disabled={creating || (locations.length === 0)} onClick={start}>
            <Plus className="h-4 w-4" /> {creating ? 'Starte…' : `${typeLabel(invType)} beginnen`}
          </Button>
        </div>

        {/* Bestehende Inventuren */}
        <div>
          <h2 className="text-base font-semibold text-foreground mb-2">Vorhandene Inventuren</h2>
          {loading ? <p className="text-sm text-muted-foreground">Lädt…</p>
            : inventories.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="empty-list">Noch keine manuellen Inventuren.</p>
            : (
              <div className="space-y-2">
                {inventories.map(inv => (
                  <button key={inv.id} data-testid={`inv-row-${inv.id}`} onClick={() => navigate(`/inventur/manuell/${inv.id}`)}
                    className="w-full flex items-center justify-between gap-3 bg-card border border-border rounded-xl px-4 py-3 text-left hover:border-teal-400">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {inv.inv_type === 'pfand' ? 'Pfand' : 'Produkte'} · {inv.location_name || '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">{inv.inventory_date}</p>
                    </div>
                    <span className={`shrink-0 text-xs px-2 py-1 rounded-full flex items-center gap-1 ${inv.status === 'draft' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                      {inv.status === 'draft' ? <><Clock className="h-3 w-3" /> Entwurf</> : <><CheckCircle2 className="h-3 w-3" /> Abgeschlossen</>}
                    </span>
                  </button>
                ))}
              </div>
            )}
        </div>

        <p className="text-[11px] text-muted-foreground text-center" data-testid="manual-ui-version">UI {MANUAL_UI_VERSION}</p>
      </div>

      {showTpl && <TemplateModal onClose={() => setShowTpl(false)} />}
      {showSL && <ManualSLPermissionModal onClose={() => setShowSL(false)} />}
    </DashboardLayout>
  );
}

// ── Vorlagenverwaltung (Admin) ───────────────────────────────────────────────
function TemplateModal({ onClose }) {
  const [tab, setTab] = useState('pfand');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=keiner, {}=neu, {...}=bearbeiten

  const load = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/manual-inventory/templates?inv_type=${t}`, authHeader());
      setList(res.data || []);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(tab); }, [tab]);

  const toggleActive = async (tpl) => {
    try {
      await axios.patch(`${API}/api/manual-inventory/templates/${tpl.id}`, { active: !tpl.active }, authHeader());
      setList(prev => prev.map(x => x.id === tpl.id ? { ...x, active: !tpl.active } : x));
    } catch { toast.error('Fehler'); }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-start sm:items-center justify-center p-3 overflow-y-auto" data-testid="template-modal">
      <div className="bg-card w-full max-w-md rounded-2xl shadow-2xl my-auto max-h-[92dvh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-card">
          <h2 className="text-base font-bold text-foreground">Vorlagen verwalten</h2>
          <button data-testid="tpl-close" onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="tpl-tab-pfand" onClick={() => { setTab('pfand'); setEditing(null); }}
              className={`py-2 rounded-lg border text-sm font-medium ${tab === 'pfand' ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/20' : 'border-border text-muted-foreground'}`}>Pfandarten</button>
            <button data-testid="tpl-tab-product" onClick={() => { setTab('product'); setEditing(null); }}
              className={`py-2 rounded-lg border text-sm font-medium ${tab === 'product' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/20' : 'border-border text-muted-foreground'}`}>Produkte</button>
          </div>

          {editing === null && (
            <Button data-testid="tpl-add-btn" variant="outline" size="sm" className="w-full gap-1.5" onClick={() => setEditing({})}>
              <Plus className="h-4 w-4" /> Neue {tab === 'pfand' ? 'Pfandart' : 'Produktvorlage'}
            </Button>
          )}

          {editing !== null && (
            <TemplateForm invType={tab} initial={editing} onCancel={() => setEditing(null)}
              onSaved={() => { setEditing(null); load(tab); }} />
          )}

          {loading ? <p className="text-sm text-muted-foreground">Lädt…</p>
            : list.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="tpl-empty">Noch keine Vorlagen.</p>
            : list.map(tpl => (
              <div key={tpl.id} data-testid={`tpl-row-${tpl.id}`} className={`flex items-center justify-between gap-2 border border-border rounded-xl px-3 py-2 ${!tpl.active ? 'opacity-60' : ''}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{tpl.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {tpl.inv_type === 'pfand'
                      ? `${centsToEur(tpl.deposit_cents)} € / Stück`
                      : `${tpl.pack_size} Stk/Pack · ${centsToEur(tpl.price_cents)} € ${tpl.price_mode === 'pack' ? '/Pack' : '/Stück'} · ${tpl.price_basis === 'net' ? 'netto' : 'brutto'} · ${tpl.tax_rate}%`}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`tpl-edit-${tpl.id}`} onClick={() => setEditing(tpl)} className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`tpl-active-${tpl.id}`} onClick={() => toggleActive(tpl)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium border ${tpl.active ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-background text-muted-foreground border-border'}`}>
                    {tpl.active ? 'Aktiv' : 'Inaktiv'}
                  </button>
                </div>
              </div>
            ))}
          <p className="text-[11px] text-muted-foreground pt-1">Vorlagenänderungen wirken nicht rückwirkend – bestehende Erfassungen behalten ihren gespeicherten Snapshot. Deaktivieren erhält die Historie.</p>
        </div>
      </div>
    </div>
  );
}

function TemplateForm({ invType, initial, onCancel, onSaved }) {
  const isEdit = !!(initial && initial.id);
  const [name, setName] = useState(initial?.name || '');
  const [deposit, setDeposit] = useState(initial?.deposit_cents != null ? centsToEur(initial.deposit_cents) : '');
  const [packSize, setPackSize] = useState(initial?.pack_size || '');
  const [price, setPrice] = useState(initial?.price_cents != null ? centsToEur(initial.price_cents) : '');
  const [priceMode, setPriceMode] = useState(initial?.price_mode || 'pack');
  const [priceBasis, setPriceBasis] = useState(initial?.price_basis || 'net');
  const [tax, setTax] = useState(initial?.tax_rate || 19);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Bezeichnung fehlt'); return; }
    setSaving(true);
    try {
      if (invType === 'pfand') {
        const dep = parseEurToCents(deposit);
        if (dep == null) { toast.error('Pfandbetrag ungültig'); setSaving(false); return; }
        const body = { name: name.trim(), deposit_cents: dep };
        if (isEdit) await axios.patch(`${API}/api/manual-inventory/templates/${initial.id}`, body, authHeader());
        else await axios.post(`${API}/api/manual-inventory/templates`, { inv_type: 'pfand', ...body }, authHeader());
      } else {
        const pc = parseEurToCents(price);
        const ps = parseInt(packSize, 10);
        if (!(ps > 0)) { toast.error('Packungsinhalt muss > 0 sein'); setSaving(false); return; }
        if (pc == null) { toast.error('Preis ungültig'); setSaving(false); return; }
        const body = { name: name.trim(), pack_size: ps, price_cents: pc, price_mode: priceMode, price_basis: priceBasis, tax_rate: Number(tax) };
        if (isEdit) await axios.patch(`${API}/api/manual-inventory/templates/${initial.id}`, body, authHeader());
        else await axios.post(`${API}/api/manual-inventory/templates`, { inv_type: 'product', ...body }, authHeader());
      }
      toast.success('Vorlage gespeichert');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const derived = (() => {
    if (invType !== 'product') return null;
    const pc = parseEurToCents(price); const ps = parseInt(packSize, 10);
    if (pc == null || !(ps > 0)) return null;
    const unit = priceMode === 'pack' ? pc / ps : pc;
    return `${priceMode === 'pack' ? 'Einzelpreis' : 'Packungspreis'} abgeleitet: ${(priceMode === 'pack' ? (unit / 100).toFixed(4) : (pc * ps / 100).toFixed(2)).replace('.', ',')} €`;
  })();

  return (
    <div className="border border-dashed border-border rounded-xl p-3 space-y-2" data-testid="tpl-form">
      <input data-testid="tpl-name" value={name} onChange={e => setName(e.target.value)} placeholder="Bezeichnung"
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm" />
      {invType === 'pfand' ? (
        <input data-testid="tpl-deposit" value={deposit} onChange={e => setDeposit(e.target.value)} inputMode="decimal" placeholder="Pfand/Stück €, z. B. 0,25"
          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="tpl-packsize" value={packSize} onChange={e => setPackSize(e.target.value)} inputMode="numeric" placeholder="Stück/Packung"
              className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm" />
            <input data-testid="tpl-price" value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" placeholder="Preis €"
              className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="tpl-pricemode" value={priceMode} onChange={e => setPriceMode(e.target.value)} className="px-2 py-2 rounded-lg border border-border bg-background text-foreground text-sm">
              <option value="pack">Packungspreis</option>
              <option value="unit">Einzelpreis</option>
            </select>
            <select data-testid="tpl-pricebasis" value={priceBasis} onChange={e => setPriceBasis(e.target.value)} className="px-2 py-2 rounded-lg border border-border bg-background text-foreground text-sm">
              <option value="net">Netto</option>
              <option value="gross">Brutto</option>
            </select>
          </div>
          <div className="flex gap-2">
            {[7, 19].map(r => (
              <button key={r} type="button" data-testid={`tpl-tax-${r}`} onClick={() => setTax(r)}
                className={`flex-1 py-2 rounded-lg border text-sm ${Number(tax) === r ? 'bg-indigo-600 text-white border-indigo-600' : 'border-border text-foreground'}`}>{r}%</button>
            ))}
          </div>
          {derived && <p className="text-[11px] text-muted-foreground" data-testid="tpl-derived">{derived}</p>}
        </>
      )}
      <div className="flex gap-2 pt-1">
        <Button data-testid="tpl-cancel" variant="outline" size="sm" className="flex-1" onClick={onCancel} disabled={saving}>Abbrechen</Button>
        <Button data-testid="tpl-save" size="sm" className="flex-1" onClick={save} disabled={saving}>{saving ? 'Speichert…' : 'Speichern'}</Button>
      </div>
    </div>
  );
}

// ── SL-Rechteverwaltung (manual_reopen_permission) ───────────────────────────
function ManualSLPermissionModal({ onClose }) {
  const [sls, setSls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/manual-inventory/sl-permissions`, authHeader())
      .then(r => setSls(r.data || [])).catch(() => toast.error('Fehler beim Laden')).finally(() => setLoading(false));
  }, []);
  const toggle = async (userId, current) => {
    setSaving(userId);
    try {
      await axios.patch(`${API}/api/manual-inventory/sl-permission/${userId}`, { enabled: !current }, authHeader());
      setSls(prev => prev.map(sl => sl.id === userId ? { ...sl, manual_reopen_permission: !current } : sl));
      toast.success('Berechtigung aktualisiert');
    } catch { toast.error('Fehler'); } finally { setSaving(null); }
  };
  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="manual-sl-perm-modal">
      <div className="bg-card w-full max-w-md rounded-2xl shadow-2xl my-auto max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-bold text-foreground">Wiederöffnen-Recht: Manuelle Inventur</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Welche Stationsleitungen dürfen abgeschlossene manuelle Inventuren wieder öffnen?</p>
          </div>
          <button data-testid="manual-sl-perm-close" onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-2">
          {loading ? <p className="text-sm text-muted-foreground">Lädt…</p>
            : sls.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="manual-sl-perm-empty">Keine aktiven Stationsleitungen vorhanden.</p>
            : sls.map(sl => (
              <div key={sl.id} data-testid={`manual-sl-perm-row-${sl.id}`} className="flex items-center justify-between gap-3 border border-border rounded-xl px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{sl.name || sl.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{sl.email}</p>
                </div>
                <button data-testid={`manual-sl-perm-toggle-${sl.id}`} disabled={saving === sl.id}
                  onClick={() => toggle(sl.id, sl.manual_reopen_permission)}
                  className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border ${sl.manual_reopen_permission ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}>
                  {saving === sl.id ? '…' : sl.manual_reopen_permission ? 'Erlaubt' : 'Gesperrt'}
                </button>
              </div>
            ))}
          <p className="text-[11px] text-muted-foreground pt-1">Eigenes Recht NUR für manuelle Inventuren (nicht Presse). Standardmäßig gesperrt. Admins dürfen immer. Änderungen wirken sofort (serverseitig live geprüft).</p>
        </div>
      </div>
    </div>
  );
}
